import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailVendorTypesTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailVendorTypes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [vendorType, setVendorType] = useState<string | null>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [retailVendorTypes, setRetailVendorTypes] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Vendor Type": vendorType,
  };

  const validateInput = () => {
    setNameError(false);
    if (!vendorType || String(vendorType).trim() === "") {
      setNameError(true);
      return false;
    }
    if (
      !validateString(
        vendorType,
        validationLength.vendorType,
        validationType.NAME,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  const editRetailVendorTypes = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setVendorType(null);
      setNameError(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setVendorType(list?.["Vendor Type"] ?? null);
    setNameError(false);
  };

  const updateRetailVendorTypes = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailVendorTypes.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.VENDORTYPE,
        vendorType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setRetailVendorTypes((prev: any[]) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailVendorTypes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.vendorTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailVendorTypes = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(retailVendorTypes, DNDFieldsEnum.VENDORTYPE, vendorType)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailVendorTypes((prev: any[]) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailVendorTypes.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailVendorTypes(null);
    setEditClicked(false);
  };

  const deleteRetailVendorTypes = (id: number) => {
    setRetailVendorTypes(deleteItem(retailVendorTypes, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailVendorTypes(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailVendorTypes);
    if (editClicked) {
      setSelectedId(
        checkData(retailVendorTypes, selectedId) ? selectedId : null,
      );
    }
  }, [retailVendorTypes]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Vendor Types
              <Tooltip
                title={tooltipComponent(retailVendorTypesTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailVendorTypes(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailVendorTypes(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Vendor Type
              <Tooltip
                title={tooltipComponent(retailVendorTypesTooltip.vendorType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailVendorType"
              variant="outlined"
              placeholder="Vendor Type"
              value={vendorType ?? ""}
              helperText={
                nameError
                  ? !vendorType || String(vendorType).trim() === ""
                    ? validationMessages.fieldRequired
                    : validationMessages.retailVendorTypeName
                  : fieldValidationHelperText.retailVendorType
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setVendorType(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId ? updateRetailVendorTypes : addRetailVendorTypes
              }
              id="addRetailVendorType"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Vendor Type
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Vendor Types Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailVendorTypes?.length ? (
                <DraggableItemList
                  itemData={retailVendorTypes}
                  onOrderUpdate={(order) => setRetailVendorTypes(order)}
                  onEdit={(info) => {
                    editRetailVendorTypes(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailVendorTypes(id)}
                  code={DNDFieldsEnum.VENDORTYPE}
                  name={""}
                />
              ) : (
                <Typography variant="body2">
                  No Vendor Types Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailVendorTypes;
